import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus,
  Edit,
  Trash2,
  ClipboardList,
  FileQuestion,
  Users,
  BarChart3,
  Eye,
  Send,
  Heart,
  Star,
  MessageSquare,
  Activity,
  Target,
  Smile,
  CheckCircle,
  Circle,
  Power,
  Settings,
  ChevronRight,
  Copy,
} from "lucide-react";
import { API } from "@/App";
import { useTranslation } from "react-i18next";

// Question types
const QUESTION_TYPES = [
  { value: "text", label: "Metin Yanıtı", icon: "📝" },
  { value: "multiple_choice", label: "Çoktan Seçmeli", icon: "☑️" },
  { value: "rating", label: "Puan (1-5)", icon: "⭐" },
  { value: "emoji", label: "Emoji Seçimi", icon: "😊" },
  { value: "yes_no", label: "Evet/Hayır", icon: "✓" },
];

// Emoji options for emoji type questions
const EMOJI_OPTIONS = ["😴", "😊", "😤", "💪", "😐", "🎯", "❤️", "👍"];

// Available icons
const ICON_MAP = {
  ClipboardList,
  Heart,
  Star,
  MessageSquare,
  Activity,
  Users,
  Target,
  Smile,
  FileQuestion,
};

const COLOR_MAP = {
  blue: "bg-blue-100 text-blue-700",
  green: "bg-green-100 text-green-700",
  purple: "bg-purple-100 text-purple-700",
  orange: "bg-orange-100 text-orange-700",
  red: "bg-red-100 text-red-700",
  pink: "bg-pink-100 text-pink-700",
  cyan: "bg-cyan-100 text-cyan-700",
  amber: "bg-amber-100 text-amber-700",
};

export default function BusinessSurveysModule({ businessId }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "qr", "surveys"]);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("active");
  
  // Data
  const [availableTypes, setAvailableTypes] = useState([]);
  const [activeSurveys, setActiveSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyStats, setSurveyStats] = useState(null);
  
  // Modals
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Forms
  const [selectedType, setSelectedType] = useState(null);
  const [customTitle, setCustomTitle] = useState("");
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [activationMode, setActivationMode] = useState("automatic"); // "automatic" or "manual"
  
  const [customForm, setCustomForm] = useState({
    title: "",
    description: "",
    target_audience: "members",
    is_recurring: false,
    recurring_trigger: "",
    send_notification: true,
    distribution_mode: "automatic", // "automatic" or "manual"
  });
  
  const [detailsForm, setDetailsForm] = useState({
    title: "",
    description: "",
    distribution_mode: "automatic",
    is_active: true,
  });
  
  const [questionForm, setQuestionForm] = useState({
    text: "",
    type: "text",
    options: [""],
    is_required: true,
  });

  useEffect(() => {
    if (businessId) {
      fetchData();
    }
  }, [businessId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [typesRes, surveysRes] = await Promise.all([
        fetch(`${API}/surveys/business/${businessId}/available-types`, {
          credentials: "include",
        }),
        fetch(`${API}/surveys/business/${businessId}`, {
          credentials: "include",
        }),
      ]);

      if (typesRes.ok) {
        const data = await typesRes.json();
        setAvailableTypes(data);
      }
      if (surveysRes.ok) {
        const data = await surveysRes.json();
        setActiveSurveys(data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyWithQuestions = async (surveyId) => {
    try {
      const response = await fetch(
        `${API}/surveys/business/${businessId}/${surveyId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setSelectedSurvey(data);
        setShowQuestionsModal(true);
      }
    } catch (error) {
      console.error("Error fetching survey:", error);
    }
  };

  const fetchSurveyStats = async (surveyId) => {
    try {
      const response = await fetch(
        `${API}/surveys/business/${businessId}/${surveyId}/stats`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setSurveyStats(data);
        setShowStatsModal(true);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // Activate a system survey type
  const handleActivateSurvey = async () => {
    if (!selectedType) return;

    try {
      const response = await fetch(
        `${API}/surveys/business/${businessId}/activate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            survey_type_id: selectedType.type_id,
            custom_title: customTitle || null,
            distribution_mode: activationMode,
          }),
        }
      );

      if (response.ok) {
        toast.success(t("surveys:toast_anketAktiflestirildi"));
        if (activationMode === "automatic") {
          toast.info(t("surveys:toast_tumUyelerBuAnketiGorebilir"));
        }
        fetchData();
        handleCloseActivateModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  // Deactivate a survey
  const handleDeactivateSurvey = async (surveyId) => {
    if (!confirm(t("surveys:confirmDisableSurvey")))
      return;

    try {
      const response = await fetch(
        `${API}/surveys/business/${businessId}/deactivate/${surveyId}`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  // Create custom survey
  const handleCreateCustomSurvey = async (e) => {
    e.preventDefault();

    if (!customForm.title.trim()) {
      toast.error(t("surveys:toast_anketBasligiGerekli"));
      return;
    }

    try {
      const response = await fetch(
        `${API}/surveys/business/${businessId}/custom`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(customForm),
        }
      );

      if (response.ok) {
        toast.success(t("surveys:toast_ozelAnketOlusturuldu"));
        if (customForm.distribution_mode === "automatic") {
          toast.info(t("surveys:toast_tumUyelerBuAnketiGorebilir"));
        }
        fetchData();
        handleCloseCustomModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  // Update survey details
  const handleUpdateSurveyDetails = async () => {
    if (!selectedSurvey) return;

    try {
      const response = await fetch(
        `${API}/surveys/business/${businessId}/${selectedSurvey.survey_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            title: detailsForm.title,
            description: detailsForm.description,
            distribution_mode: detailsForm.distribution_mode,
          }),
        }
      );

      if (response.ok) {
        toast.success(t("surveys:toast_anketGuncellendi"));
        fetchData();
        setShowDetailsModal(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  // Open details modal
  const handleOpenDetailsModal = (survey) => {
    setSelectedSurvey(survey);
    setDetailsForm({
      title: survey.title,
      description: survey.description || "",
      distribution_mode: survey.distribution_mode || "automatic",
      is_active: survey.is_active !== false,
    });
    setShowDetailsModal(true);
  };

  // Delete survey
  const handleDeleteSurvey = async (surveyId) => {
    if (
      !confirm(
        t("surveys:confirmDeleteOnlyEmpty")
      )
    )
      return;

    try {
      const response = await fetch(
        `${API}/surveys/business/${businessId}/${surveyId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        toast.success("Anket silindi");
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Silinemedi");
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  // Question CRUD
  const handleSaveQuestion = async (e) => {
    e.preventDefault();

    if (!questionForm.text.trim()) {
      toast.error(t('common:questionTextRequired'));
      return;
    }

    try {
      const url = editingQuestion
        ? `${API}/surveys/business/${businessId}/${selectedSurvey.survey_id}/questions/${editingQuestion.question_id}`
        : `${API}/surveys/business/${businessId}/${selectedSurvey.survey_id}/questions`;

      // Prepare options based on question type
      let options = null;
      if (questionForm.type === "multiple_choice") {
        options = questionForm.options.filter((o) => o.trim());
        if (options.length < 2) {
          toast.error(t("common:toast_enAz2SecenekGerekli"));
          return;
        }
      } else if (questionForm.type === "emoji") {
        options = EMOJI_OPTIONS;
      }

      const response = await fetch(url, {
        method: editingQuestion ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...questionForm,
          options,
        }),
      });

      if (response.ok) {
        toast.success(editingQuestion ? t("surveys:questionUpdated") : t("surveys:questionAdded"));
        await fetchSurveyWithQuestions(selectedSurvey.survey_id);
        handleCloseQuestionModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!confirm(t("surveys:confirmDeleteQuestion"))) return;

    try {
      const response = await fetch(
        `${API}/surveys/business/${businessId}/${selectedSurvey.survey_id}/questions/${questionId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        toast.success(t('common:questionDeleted'));
        await fetchSurveyWithQuestions(selectedSurvey.survey_id);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  // Modal handlers
  const handleOpenActivateModal = (type) => {
    setSelectedType(type);
    setCustomTitle("");
    setActivationMode("automatic");
    setShowActivateModal(true);
  };

  const handleCloseActivateModal = () => {
    setShowActivateModal(false);
    setSelectedType(null);
    setCustomTitle("");
    setActivationMode("automatic");
  };

  const handleCloseCustomModal = () => {
    setShowCustomModal(false);
    setCustomForm({
      title: "",
      description: "",
      target_audience: "members",
      is_recurring: false,
      recurring_trigger: "",
      send_notification: true,
      distribution_mode: "automatic",
    });
  };

  const handleOpenQuestionModal = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      setQuestionForm({
        text: question.text,
        type: question.type,
        options: question.options || [""],
        is_required: question.is_required,
      });
    } else {
      setEditingQuestion(null);
      setQuestionForm({
        text: "",
        type: "text",
        options: [""],
        is_required: true,
      });
    }
    setShowQuestionModal(true);
  };

  const handleCloseQuestionModal = () => {
    setShowQuestionModal(false);
    setEditingQuestion(null);
  };

  const getIconComponent = (iconName) => {
    return ICON_MAP[iconName] || ClipboardList;
  };

  const getColorClass = (colorName) => {
    return COLOR_MAP[colorName] || "bg-blue-100 text-blue-700";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }

  const inactiveTypes = availableTypes.filter((t) => !t.is_activated);
  const systemSurveys = activeSurveys.filter((s) => s.survey_type_id);
  const customSurveys = activeSurveys.filter((s) => !s.survey_type_id);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy">{t("surveys:anketYonetimi")}</h1>
          <p className="text-grey text-sm">
            Üyeleriniz için anketler oluşturun ve yönetin
          </p>
        </div>
        <Button
          onClick={() => setShowCustomModal(true)}
          className="bg-navy hover:bg-navy/90 rounded-xl"
          data-testid="create-custom-survey-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Özel Anket Oluştur
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 rounded-xl">
          <TabsTrigger value="active" className="rounded-xl">
            <CheckCircle className="w-4 h-4 mr-2" />
            Aktif Anketler ({activeSurveys.length})
          </TabsTrigger>
          <TabsTrigger value="templates" className="rounded-xl">
            <Copy className="w-4 h-4 mr-2" />
            Şablonlar ({inactiveTypes.length})
          </TabsTrigger>
        </TabsList>

        {/* Active Surveys Tab */}
        <TabsContent value="active" className="mt-4 space-y-4">
          {activeSurveys.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-12 text-center">
                <ClipboardList className="w-12 h-12 mx-auto text-grey/50 mb-4" />
                <p className="text-grey mb-4">{t("surveys:henuzAktifAnketYok")}</p>
                <div className="flex justify-center gap-2">
                  <Button
                    onClick={() => setActiveTab("templates")}
                    variant="outline"
                    className="rounded-xl"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Şablondan Ekle
                  </Button>
                  <Button
                    onClick={() => setShowCustomModal(true)}
                    className="bg-navy hover:bg-navy/90 rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Özel Oluştur
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* System Surveys */}
              {systemSurveys.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-grey mb-2">
                    Sistem Anketleri
                  </h3>
                  <div className="grid gap-3">
                    {systemSurveys.map((survey) => {
                      const IconComp = getIconComponent(survey.icon);
                      return (
                        <Card
                          key={survey.survey_id}
                          className="border-0 shadow-sm hover:shadow-md transition-shadow"
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4 flex-1">
                                <div
                                  className={`p-3 rounded-xl ${getColorClass(
                                    survey.color
                                  )}`}
                                >
                                  <IconComp className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-navy truncate">
                                    {survey.title}
                                  </h3>
                                  <div className="flex items-center gap-3 mt-1 text-sm text-grey">
                                    <span>{survey.type_name || "Sistem"}</span>
                                    <span>•</span>
                                    <span>{survey.question_count || 0} soru</span>
                                    <span>•</span>
                                    <span>{survey.response_count || 0} yanıt</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenDetailsModal(survey)}
                                  className="rounded-xl"
                                >
                                  <Settings className="w-4 h-4 mr-1" />
                                  Detaylar
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    fetchSurveyWithQuestions(survey.survey_id)
                                  }
                                  className="rounded-xl"
                                >
                                  <FileQuestion className="w-4 h-4 mr-1" />
                                  Sorular
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedSurvey(survey);
                                    fetchSurveyStats(survey.survey_id);
                                  }}
                                  className="rounded-xl"
                                >
                                  <BarChart3 className="w-4 h-4 mr-1" />
                                  İstatistik
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleDeactivateSurvey(survey.survey_id)
                                  }
                                  className="h-8 w-8 p-0 text-orange-500"
                                  title={t("admin:ph_devreDisiBirak")}
                                >
                                  <Power className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Custom Surveys */}
              {customSurveys.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-grey mb-2">
                    Özel Anketler
                  </h3>
                  <div className="grid gap-3">
                    {customSurveys.map((survey) => (
                      <Card
                        key={survey.survey_id}
                        className="border-0 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 flex-1">
                              <div className="p-3 rounded-xl bg-purple-100 text-purple-700">
                                <FileQuestion className="w-5 h-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-navy truncate">
                                    {survey.title}
                                  </h3>
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">
                                    Özel
                                  </Badge>
                                  {survey.is_recurring && (
                                    <Badge className="bg-blue-100 text-blue-700 text-xs">
                                      Tekrarlayan
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-sm text-grey">
                                  <span>{survey.question_count || 0} soru</span>
                                  <span>•</span>
                                  <span>{survey.response_count || 0} yanıt</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenDetailsModal(survey)}
                                className="rounded-xl"
                              >
                                <Settings className="w-4 h-4 mr-1" />
                                Detaylar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  fetchSurveyWithQuestions(survey.survey_id)
                                }
                                className="rounded-xl"
                              >
                                <FileQuestion className="w-4 h-4 mr-1" />
                                Sorular
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedSurvey(survey);
                                  fetchSurveyStats(survey.survey_id);
                                }}
                                className="rounded-xl"
                              >
                                <BarChart3 className="w-4 h-4 mr-1" />
                                İstatistik
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleDeleteSurvey(survey.survey_id)
                                }
                                className="h-8 w-8 p-0 text-red-500"
                                title={t('common:delete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Copy className="w-5 h-5" />
                Kullanılabilir Anket Şablonları
              </CardTitle>
              <p className="text-sm text-grey">
                Sistem tarafından sağlanan hazır anket şablonlarını aktifleştirin
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {inactiveTypes.length === 0 ? (
                <div className="text-center py-8 text-grey">
                  <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                  <p>Tüm şablonlar aktif!</p>
                </div>
              ) : (
                inactiveTypes.map((type) => {
                  const IconComp = getIconComponent(type.icon);
                  return (
                    <div
                      key={type.type_id}
                      className="flex items-center justify-between p-4 border rounded-xl hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`p-3 rounded-xl ${getColorClass(
                            type.color
                          )}`}
                        >
                          <IconComp className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-medium text-navy">{type.name}</h4>
                          <p className="text-sm text-grey">
                            {type.description}
                          </p>
                          <p className="text-xs text-grey mt-1">
                            {type.default_question_count || 0} varsayılan soru
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleOpenActivateModal(type)}
                        className="bg-green-600 hover:bg-green-700 rounded-xl"
                      >
                        <Power className="w-4 h-4 mr-2" />
                        Aktifleştir
                      </Button>
                    </div>
                  );
                })
              )}

              {/* Show already activated types */}
              {availableTypes.filter((t) => t.is_activated).length > 0 && (
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-grey mb-2">
                    Zaten Aktif Olanlar
                  </p>
                  {availableTypes
                    .filter((t) => t.is_activated)
                    .map((type) => {
                      const IconComp = getIconComponent(type.icon);
                      return (
                        <div
                          key={type.type_id}
                          className="flex items-center gap-4 p-3 bg-green-50 rounded-xl mb-2"
                        >
                          <div
                            className={`p-2 rounded-lg ${getColorClass(
                              type.color
                            )}`}
                          >
                            <IconComp className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <span className="font-medium text-green-800">
                              {type.custom_title || type.name}
                            </span>
                          </div>
                          <Badge className="bg-green-200 text-green-800">
                            Aktif
                          </Badge>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Activate Survey Modal */}
      <Dialog open={showActivateModal} onOpenChange={setShowActivateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("surveys:anketiAktiflestir")}</DialogTitle>
          </DialogHeader>
          {selectedType && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                {(() => {
                  const IconComp = getIconComponent(selectedType.icon);
                  return (
                    <div
                      className={`p-3 rounded-xl ${getColorClass(
                        selectedType.color
                      )}`}
                    >
                      <IconComp className="w-6 h-6" />
                    </div>
                  );
                })()}
                <div>
                  <h3 className="font-semibold text-navy">{selectedType.name}</h3>
                  <p className="text-sm text-grey">{selectedType.description}</p>
                </div>
              </div>

              <div>
                <Label>Özel Başlık (Opsiyonel)</Label>
                <Input
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder={selectedType.name}
                  className="rounded-xl mt-1"
                />
                <p className="text-xs text-grey mt-1">
                  Boş bırakırsanız varsayılan isim kullanılır
                </p>
              </div>

              {/* Distribution Mode */}
              <div>
                <Label className="mb-2 block">{t("common:distributionMode")}</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setActivationMode("automatic")}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      activationMode === "automatic"
                        ? "border-green-500 bg-green-50"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-sm">Otomatik</span>
                    </div>
                    <p className="text-xs text-grey">{t("qr:allMembersCanSee")}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActivationMode("manual")}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      activationMode === "manual"
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Send className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-sm">Manuel</span>
                    </div>
                    <p className="text-xs text-grey">{t("common:sentToSelected")}</p>
                  </button>
                </div>
              </div>

              <div className="p-3 bg-blue-50 rounded-xl">
                <p className="text-sm text-blue-800">
                  Bu anket aktifleştirildiğinde, {selectedType.default_question_count || 0}{" "}
                  varsayılan soru ile oluşturulacak. Daha sonra soruları
                  düzenleyebilir, ekleyebilir veya kaldırabilirsiniz.
                </p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={handleCloseActivateModal}
                  className="flex-1 rounded-xl"
                >
                  İptal
                </Button>
                <Button
                  onClick={handleActivateSurvey}
                  className="flex-1 bg-green-600 hover:bg-green-700 rounded-xl"
                >
                  <Power className="w-4 h-4 mr-2" />
                  Aktifleştir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Custom Survey Modal */}
      <Dialog open={showCustomModal} onOpenChange={setShowCustomModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("surveys:ozelAnketOlustur")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCustomSurvey} className="space-y-4">
            <div>
              <Label>Anket Başlığı *</Label>
              <Input
                value={customForm.title}
                onChange={(e) =>
                  setCustomForm({ ...customForm, title: e.target.value })
                }
                placeholder={t("common:ph_ornHaftalikGeriBildirim")}
                className="rounded-xl mt-1"
              />
            </div>

            <div>
              <Label>{t('common:description')}</Label>
              <Textarea
                value={customForm.description}
                onChange={(e) =>
                  setCustomForm({ ...customForm, description: e.target.value })
                }
                placeholder={t("surveys:ph_anketHakkindaKisaBirAciklama")}
                className="rounded-xl mt-1"
                rows={2}
              />
            </div>

            <div>
              <Label>Hedef Kitle</Label>
              <Select
                value={customForm.target_audience}
                onValueChange={(v) =>
                  setCustomForm({ ...customForm, target_audience: v })
                }
              >
                <SelectTrigger className="rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Herkes</SelectItem>
                  <SelectItem value="members">{t("common:sadeceUyeler")}</SelectItem>
                  <SelectItem value="trainers">{t("common:sadeceAntrenorler")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Distribution Mode */}
            <div>
              <Label className="mb-2 block">{t("common:distributionMode")}</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setCustomForm({ ...customForm, distribution_mode: "automatic" })}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    customForm.distribution_mode === "automatic"
                      ? "border-green-500 bg-green-50"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-sm">Otomatik</span>
                  </div>
                  <p className="text-xs text-grey">{t("qr:allMembersCanSee")}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setCustomForm({ ...customForm, distribution_mode: "manual" })}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    customForm.distribution_mode === "manual"
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Send className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-sm">Manuel</span>
                  </div>
                  <p className="text-xs text-grey">{t("common:sentToSelected")}</p>
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <div>
                <p className="font-medium text-navy text-sm">Tekrarlayan Anket</p>
                <p className="text-xs text-grey">{t("common:herDersSonrasiVeyaPeriyodik")}</p>
              </div>
              <Switch
                checked={customForm.is_recurring}
                onCheckedChange={(c) =>
                  setCustomForm({ ...customForm, is_recurring: c })
                }
              />
            </div>

            {customForm.is_recurring && (
              <div>
                <Label>{t("common:repeatTime")}</Label>
                <Select
                  value={customForm.recurring_trigger}
                  onValueChange={(v) =>
                    setCustomForm({ ...customForm, recurring_trigger: v })
                  }
                >
                  <SelectTrigger className="rounded-xl mt-1">
                    <SelectValue placeholder={t("common:ph_secin")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="after_session">{t("common:herDersSonrasi")}</SelectItem>
                    <SelectItem value="weekly">{t("common:weekly")}</SelectItem>
                    <SelectItem value="monthly">{t("common:monthly")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
              <div>
                <p className="font-medium text-blue-800 text-sm">
                  Üyelere Bildirim Gönder
                </p>
                <p className="text-xs text-blue-600">Yeni anketten haberdar et</p>
              </div>
              <Switch
                checked={customForm.send_notification}
                onCheckedChange={(c) =>
                  setCustomForm({ ...customForm, send_notification: c })
                }
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseCustomModal}
                className="flex-1 rounded-xl"
              >
                İptal
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
              >
                Oluştur
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Questions Modal */}
      <Dialog open={showQuestionsModal} onOpenChange={setShowQuestionsModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileQuestion className="w-5 h-5" />
              {selectedSurvey?.title} - Sorular
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Add Question Button */}
            <Button
              onClick={() => handleOpenQuestionModal()}
              className="w-full bg-navy hover:bg-navy/90 rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              Yeni Soru Ekle
            </Button>

            {/* Questions List */}
            {selectedSurvey?.questions?.length === 0 ? (
              <div className="text-center py-8 text-grey">
                <FileQuestion className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>{t("surveys:noQuestionsYet")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedSurvey?.questions?.map((q, idx) => (
                  <div
                    key={q.question_id}
                    className="flex items-center gap-3 p-3 border rounded-xl hover:bg-slate-50"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-navy text-white text-sm font-medium">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-navy">{q.text}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {QUESTION_TYPES.find((t) => t.value === q.type)?.label ||
                            q.type}
                        </Badge>
                        {q.is_required && (
                          <Badge className="bg-red-100 text-red-700 text-xs">
                            Zorunlu
                          </Badge>
                        )}
                        {q.is_customized && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">
                            Düzenlendi
                          </Badge>
                        )}
                        {q.is_custom && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                            Özel
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenQuestionModal(q)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteQuestion(q.question_id)}
                        className="h-8 w-8 p-0 text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Question Create/Edit Modal */}
      <Dialog open={showQuestionModal} onOpenChange={setShowQuestionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion ? t('surveys:editQuestion') : t('common:addNewQuestion')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveQuestion} className="space-y-4">
            <div>
              <Label>{t("surveys:questionText")}</Label>
              <Textarea
                value={questionForm.text}
                onChange={(e) =>
                  setQuestionForm({ ...questionForm, text: e.target.value })
                }
                placeholder={t("surveys:ph_sorunuzuYazin")}
                className="rounded-xl mt-1"
                rows={2}
              />
            </div>

            <div>
              <Label>{t("surveys:questionType")}</Label>
              <Select
                value={questionForm.type}
                onValueChange={(v) =>
                  setQuestionForm({ ...questionForm, type: v })
                }
              >
                <SelectTrigger className="rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUESTION_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className="flex items-center gap-2">
                        <span>{type.icon}</span>
                        <span>{type.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Multiple choice options */}
            {questionForm.type === "multiple_choice" && (
              <div>
                <Label>{t("common:options")}</Label>
                <div className="space-y-2 mt-2">
                  {questionForm.options.map((opt, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        value={opt}
                        onChange={(e) => {
                          const newOptions = [...questionForm.options];
                          newOptions[idx] = e.target.value;
                          setQuestionForm({ ...questionForm, options: newOptions });
                        }}
                        placeholder={`Seçenek ${idx + 1}`}
                        className="rounded-xl"
                      />
                      {questionForm.options.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newOptions = questionForm.options.filter(
                              (_, i) => i !== idx
                            );
                            setQuestionForm({ ...questionForm, options: newOptions });
                          }}
                          className="text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setQuestionForm({
                        ...questionForm,
                        options: [...questionForm.options, ""],
                      })
                    }
                    className="rounded-xl"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Seçenek Ekle
                  </Button>
                </div>
              </div>
            )}

            {/* Emoji preview */}
            {questionForm.type === "emoji" && (
              <div>
                <Label>Emoji Seçenekleri (Önizleme)</Label>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {EMOJI_OPTIONS.map((emoji) => (
                    <span
                      key={emoji}
                      className="text-2xl p-2 bg-slate-100 rounded-lg"
                    >
                      {emoji}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <div>
                <p className="font-medium text-navy text-sm">Zorunlu Soru</p>
                <p className="text-xs text-grey">Yanıtlanması zorunlu olsun mu?</p>
              </div>
              <Switch
                checked={questionForm.is_required}
                onCheckedChange={(c) =>
                  setQuestionForm({ ...questionForm, is_required: c })
                }
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseQuestionModal}
                className="flex-1 rounded-xl"
              >
                İptal
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
              >
                {editingQuestion ? t('settings:update') : t('common:add')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stats Modal */}
      <Dialog open={showStatsModal} onOpenChange={setShowStatsModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              {selectedSurvey?.title} - İstatistikler
            </DialogTitle>
          </DialogHeader>

          {surveyStats && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-blue-700">
                  {surveyStats.total_responses}
                </p>
                <p className="text-sm text-blue-600">{t("common:totalResponses")}</p>
              </div>

              {Object.entries(surveyStats.questions || {}).map(([qId, qStats]) => (
                <div key={qId} className="border rounded-xl p-4">
                  <p className="font-medium text-navy mb-2">{qStats.text}</p>
                  {qStats.average && (
                    <div className="mb-2 flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-500" />
                      <span className="text-amber-600 font-medium">
                        Ortalama: {qStats.average}
                      </span>
                    </div>
                  )}
                  <div className="space-y-1">
                    {Object.entries(qStats.answers || {}).map(([answer, count]) => (
                      <div
                        key={answer}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm text-grey">{answer}</span>
                        <Badge variant="outline">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Anket Detayları
            </DialogTitle>
          </DialogHeader>
          {selectedSurvey && (
            <div className="space-y-4">
              <div>
                <Label>{t("surveys:anketBasligi")}</Label>
                <Input
                  value={detailsForm.title}
                  onChange={(e) =>
                    setDetailsForm({ ...detailsForm, title: e.target.value })
                  }
                  className="rounded-xl mt-1"
                />
              </div>

              <div>
                <Label>{t('common:description')}</Label>
                <Textarea
                  value={detailsForm.description}
                  onChange={(e) =>
                    setDetailsForm({ ...detailsForm, description: e.target.value })
                  }
                  placeholder={t("surveys:ph_anketHakkindaKisaBirAciklama")}
                  className="rounded-xl mt-1"
                  rows={2}
                />
              </div>

              {/* Distribution Mode */}
              <div>
                <Label className="mb-2 block">{t("common:distributionMode")}</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setDetailsForm({ ...detailsForm, distribution_mode: "automatic" })
                    }
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      detailsForm.distribution_mode === "automatic"
                        ? "border-green-500 bg-green-50"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-sm">Otomatik</span>
                    </div>
                    <p className="text-xs text-grey">{t("qr:allMembersCanSee")}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setDetailsForm({ ...detailsForm, distribution_mode: "manual" })
                    }
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      detailsForm.distribution_mode === "manual"
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Send className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-sm">Manuel</span>
                    </div>
                    <p className="text-xs text-grey">{t("common:sentToSelected")}</p>
                  </button>
                </div>
              </div>

              {/* Info */}
              <div className="p-3 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-grey">Soru Sayısı:</span>
                  <span className="font-medium">{selectedSurvey.question_count || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-grey">Yanıt Sayısı:</span>
                  <span className="font-medium">{selectedSurvey.response_count || 0}</span>
                </div>
                {selectedSurvey.type_name && (
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-grey">Şablon:</span>
                    <span className="font-medium">{selectedSurvey.type_name}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDetailsModal(false)}
                  className="flex-1 rounded-xl"
                >
                  İptal
                </Button>
                <Button
                  onClick={handleUpdateSurveyDetails}
                  className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                >
                  Kaydet
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
